import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Alert } from '../ui/alert';

/**
 * "Continue with Google" — the primary way into ITA6.
 *
 * Two deliberate details:
 *
 *   1. The button is only rendered once the SERVER says Google sign-in is
 *      configured. A button that always fails is worse than no button, and an
 *      operator who has not yet set the OAuth secrets should see that state
 *      reflected rather than have users discover it one at a time.
 *
 *   2. It is a real link (`<a href>`), not a fetch. The flow is a top-level
 *      browser navigation through Google and back, which is what makes the
 *      `SameSite=Lax` state cookie survive, and what lets the browser show and
 *      manage the redirect correctly. Fetching it would break both.
 */

/** Reason codes the callback redirects with, mapped to something a person can act on. */
const REASON_KEY: Record<string, string> = {
  cancelled: 'login.googleError.cancelled',
  google_not_configured: 'login.googleError.unavailable',
  google_not_configured_callback: 'login.googleError.unavailable',
  email_already_linked: 'login.googleError.accountConflict',
  account_not_active: 'login.googleError.accountInactive',
  identity_link_race: 'login.googleError.accountConflict',
  account_resolution_failed: 'login.googleError.accountConflict',
};

export function GoogleSignInButton({ label }: { label?: string }) {
  const { t } = useTranslation();
  const [state, setState] = useState<'checking' | 'ready' | 'unavailable'>('checking');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const payload = await api.get<{ enabled: boolean }>('/auth/google/status');
        if (!cancelled) setState(payload.enabled ? 'ready' : 'unavailable');
      } catch {
        if (!cancelled) setState('unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'unavailable') {
    return <Alert tone="info">{t('login.googleError.unavailable')}</Alert>;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      fullWidth
      loading={state === 'checking'}
      onClick={() => {
        // Full-page navigation by design; see the note above.
        window.location.assign('/api/auth/google/start');
      }}
    >
      <GoogleGlyph />
      {label ?? t('login.google')}
    </Button>
  );
}

/** The sign-in failure banner shown after a redirect back with ?error=... */
export function GoogleErrorNotice({ reason }: { reason: string | null }) {
  const { t } = useTranslation();

  const message = useMemo(() => {
    if (!reason) return null;
    const key = REASON_KEY[reason];
    // An unmapped reason is deliberately generic: the specific code would tell a
    // prober which check failed, and the user cannot act on it either way.
    return key ? t(key as never) : t('login.googleError.failed');
  }, [reason, t]);

  if (!message) return null;
  return <Alert tone="error">{message}</Alert>;
}

/** Google's mark, inlined so the sign-in page makes no third-party request. */
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="me-2">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
